import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "../../page.css";
import "../../home.css";
import "../../trekking/kilimanjaro.css";
import "./serengeti.css";
import "./tarangire.css";

const features = [
  {
    title: "Elephant country",
    text: "Tarangire is famous for large elephant herds, especially in the dry months when families gather along the river and swamp edges.",
  },
  {
    title: "Baobab landscapes",
    text: "Ancient baobabs rise from open woodland and grassland. The park feels sculptural, open, and different from Serengeti plains.",
  },
  {
    title: "River and swamp life",
    text: "The Tarangire River and Silale Swamp hold water when the bush dries out, so wildlife concentrates and birdlife stays rich.",
  },
  {
    title: "Quieter northern stop",
    text: "Fewer vehicles than Serengeti or the crater on many weeks. A strong start or finish to a northern circuit with a calmer pace.",
  },
];

const parkStats = [
  { value: "2,850", label: "km² of park" },
  { value: "2,500+", label: "elephants in season" },
  { value: "550+", label: "bird species" },
  { value: "Dry months", label: "peak game density" },
];

const experiences = [
  {
    id: "river-drives",
    name: "Tarangire River Drives",
    days: "1 to 3 nights",
    style: "Classic game drives",
    lodging: "Lodge or tented camp",
    bestFor: "Elephants, baobabs, and dry season wildlife",
    image:
      "https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Wildlife and open savanna under wide African sky",
    summary:
      "Follow the river corridor where elephants, zebra, wildebeest, and predators gather when water is scarce elsewhere in the ecosystem.",
    body: "River drives are the heart of a Tarangire stay. Guides work the watercourses, baobab groves, and open clearings in morning and late afternoon light. In the dry season the park can feel crowded with animals rather than vehicles, which is why many private circuits open or close here.",
    highlights: [
      "Large elephant herds along the river",
      "Baobab silhouettes at sunrise and sunset",
      "Lions and other predators near concentrated prey",
      "Excellent photography in open woodland",
    ],
    itinerary: [
      { day: "Morning", text: "Early drive toward the river and known elephant routes." },
      { day: "Midday", text: "Return to camp for rest during the heat." },
      { day: "Afternoon", text: "Second drive along water, swamp edges, and baobab country." },
      { day: "Optional", text: "Night drive where camp concessions allow." },
    ],
    notes:
      "Two nights give you a full day plus flexible mornings. One night still works as a sharp gateway stop.",
  },
  {
    id: "silale-swamp",
    name: "Silale Swamp and Wetlands",
    days: "Half day to full day",
    style: "Birding and big game",
    lodging: "Park lodge or nearby camp",
    bestFor: "Birdlife, buffalo, and dry season concentrations",
    image:
      "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Open wetland and grassland landscape",
    summary:
      "The swamp holds moisture deep into the dry months. Buffalo herds, elephants, and waterbirds use the edges while predators watch from nearby cover.",
    body: "Silale is where Tarangire feels especially rich in the dry season. The light is soft on the water margins, and patient drives can turn up unusual birds alongside classic big game. It pairs well with a river morning so the day covers both habitats.",
    highlights: [
      "Strong dry season wildlife density",
      "Outstanding for birders and mixed interest groups",
      "Buffalo and elephant in open wetland settings",
      "A quieter alternative to busier northern parks",
    ],
    itinerary: [
      { day: "Morning", text: "Drive from camp toward swamp margins and open flats." },
      { day: "Late morning", text: "Slow birding and game viewing along the water." },
      { day: "Picnic", text: "Lunch at an approved site when doing a full day." },
      { day: "Return", text: "Loop back via woodland tracks to camp." },
    ],
    notes:
      "Swamp access and water levels change with the season. Your guide adjusts the route day by day.",
  },
  {
    id: "baobab-woodland",
    name: "Baobab Woodland Trails",
    days: "Morning or afternoon drives",
    style: "Scenic game drives",
    lodging: "Lodge or tented camp",
    bestFor: "Landscape, light, and a classic Tarangire look",
    image:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Open woodland and dramatic sky",
    summary:
      "Open woodland dotted with baobabs gives Tarangire its signature look. Drives here are as much about atmosphere as about checklist sightings.",
    body: "Baobab country is where the park feels most itself. Giraffe move between trees, elephants strip bark, and the light turns gold early and late. We keep time for slower photography stops because the landscape is half the reason people remember Tarangire.",
    highlights: [
      "Iconic baobab scenery",
      "Giraffe, impala, and mixed woodland game",
      "Beautiful for sunrise and sunset drives",
      "A calmer contrast to Serengeti open plains",
    ],
    itinerary: [
      { day: "Sunrise", text: "Leave camp for baobab groves in soft first light." },
      { day: "Morning", text: "Work woodland loops and open clearings." },
      { day: "Afternoon", text: "Return for golden hour among the trees." },
      { day: "Evening", text: "Sundowner stop where camp rules allow." },
    ],
    notes:
      "Ideal when you want stronger landscape moments in a northern safari, not only migration drama.",
  },
  {
    id: "circuit-link",
    name: "Northern Circuit Gateway",
    days: "1 to 2 nights",
    style: "Start or finish stop",
    lodging: "Inside park or nearby lodge",
    bestFor: "Opening or closing a Serengeti and crater loop",
    image:
      "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Open highland road toward distant horizons",
    summary:
      "Tarangire sits close to Arusha roads, so it works cleanly as the first wildlife night after arrival or a last bush stop before flying out or continuing to Zanzibar.",
    body: "Logistics are simple compared with remote Serengeti camps. You can land in Arusha, reach Tarangire the same day, and settle into game drives without a long flight. Many guests then continue to Manyara, Ngorongoro, and Serengeti, or reverse the loop and end here before the coast.",
    highlights: [
      "Short transfer from Arusha compared with western Serengeti",
      "Strong dry season payoff for limited nights",
      "Easy pairing with Lake Manyara and the crater",
      "Useful when arrival timing is tight",
    ],
    itinerary: [
      { day: "Arrival", text: "Transfer from Arusha and afternoon game drive." },
      { day: "Full day", text: "River and swamp drives with picnic or camp lunch." },
      { day: "Next", text: "Continue to Manyara, Ngorongoro, or Serengeti." },
      { day: "Or exit", text: "Return to Arusha for flights or a Zanzibar connection." },
    ],
    notes:
      "Tell us your arrival day and season. We will place Tarangire at the start or end where it works hardest.",
  },
];

const seasons = [
  {
    title: "June to October",
    text: "Peak dry season. Wildlife crowds the river and swamp. The classic time for elephants and open viewing.",
  },
  {
    title: "November to December",
    text: "Short rains begin. Greener bush, good birding, and still solid game with fewer visitors in some weeks.",
  },
  {
    title: "January to March",
    text: "Longer green season. Animals spread out, birdlife stays excellent, and landscapes look soft and full.",
  },
  {
    title: "April to May",
    text: "Heavier rains possible. Some camps quieten. Beautiful when accessible, better for flexible travelers.",
  },
];

const practical = [
  {
    title: "How long to stay",
    text: "One night works as a gateway. Two nights let you cover river, swamp, and baobab country without rushing.",
  },
  {
    title: "Best months",
    text: "June to October for density. Green months still reward birders and guests who prefer quieter roads.",
  },
  {
    title: "What we arrange",
    text: "Park fees, lodge or tented camp, private 4x4 and guide, and the link into your wider northern circuit.",
  },
];

function Tarangire() {
  const { hash } = useLocation();
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    document.title = "Tarangire National Park · Mirrorkhan Tours";
  }, []);

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [hash]);

  useEffect(() => {
    const node = statsRef.current;
    if (!node) return undefined;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setStatsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setStatsVisible(true);
        observer.disconnect();
      },
      { threshold: 0.25 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="kili-page tarangire-page">
      <section className="kili-hero serengeti-hero tarangire-hero">
        <div className="kili-hero__media" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1800&q=80"
            alt=""
          />
        </div>
        <div className="kili-hero__shade" aria-hidden="true" />
        <div className="container kili-hero__content">
          <div className="row">
            <div className="col-lg-8 col-xl-7">
              <p className="eyebrow eyebrow--light mb-3">
                <Link
                  to="/safaris/northern-circuits"
                  className="text-decoration-none"
                >
                  Northern Circuit
                </Link>
              </p>
              <h1 className="mb-3">Tarangire National Park</h1>
              <p className="kili-hero__lede mb-4">
                Baobab woodland and elephant herds along the river. A quieter
                northern park that shines in the dry months and pairs cleanly
                with Manyara, Ngorongoro, and Serengeti.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <a className="btn btn-mk-gold" href="#experiences">
                  View experiences
                </a>
                <Link className="btn btn-mk-ghost" to="/contact">
                  Ask about Tarangire
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={statsRef}
        className={
          statsVisible ? "serengeti-stats is-visible" : "serengeti-stats"
        }
        aria-label="Tarangire park facts"
      >
        <div className="container">
          <ul className="serengeti-stats__list list-unstyled mb-0">
            {parkStats.map((stat, index) => (
              <li
                className="serengeti-stats__item"
                key={stat.label}
                style={{ "--stat-delay": `${index * 120}ms` }}
              >
                <span className="serengeti-stats__value">{stat.value}</span>
                <span className="serengeti-stats__label">{stat.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">The park</p>
              <h2 className="mb-3">Why Tarangire belongs on a northern safari</h2>
              <p className="page-lead mb-0">
                It is not a smaller Serengeti. It is its own landscape of
                baobabs, river corridors, and dry season gatherings that feel
                intimate and full.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {features.map((item) => (
              <div className="col-md-6" key={item.title}>
                <article className="kili-feature h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="experiences" className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">Experiences</p>
              <h2 className="mb-3">How to spend your Tarangire days</h2>
              <p className="page-lead mb-0">
                River corridors, swamp margins, and baobab woodland each give a
                different mood. We shape the drives around season and how many
                nights you have.
              </p>
            </div>
          </div>

          <div className="kili-routes">
            {experiences.map((item, index) => (
              <article
                className={
                  index % 2 === 1
                    ? "kili-route kili-route--flip"
                    : "kili-route"
                }
                key={item.id}
                id={item.id}
              >
                <div className="kili-route__top">
                  <div className="kili-route__media">
                    <img src={item.image} alt={item.imageAlt} />
                  </div>
                  <div className="kili-route__intro">
                    <p className="kili-route__meta mb-2">
                      {item.days}
                      <span aria-hidden="true"> · </span>
                      {item.style}
                    </p>
                    <h3 className="h3 mb-3">{item.name}</h3>
                    <p className="kili-route__summary">{item.summary}</p>
                    <p className="kili-route__body-copy mb-0">{item.body}</p>
                  </div>
                </div>

                <div className="kili-route__panel">
                  <dl className="kili-route__facts">
                    <div>
                      <dt>Best for</dt>
                      <dd>{item.bestFor}</dd>
                    </div>
                    <div>
                      <dt>Time</dt>
                      <dd>{item.days}</dd>
                    </div>
                    <div>
                      <dt>Lodging</dt>
                      <dd>{item.lodging}</dd>
                    </div>
                    <div>
                      <dt>Style</dt>
                      <dd>{item.style}</dd>
                    </div>
                  </dl>

                  <div className="row g-4">
                    <div className="col-lg-5">
                      <h4 className="kili-route__subhead">On the day</h4>
                      <ul className="trek-points mb-0">
                        {item.highlights.map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="col-lg-7">
                      <h4 className="kili-route__subhead">Sample outline</h4>
                      <ol className="kili-itinerary list-unstyled mb-0">
                        {item.itinerary.map((step) => (
                          <li key={step.day}>
                            <strong>{step.day}</strong>
                            <span>{step.text}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>

                  <p className="kili-route__note mb-3">{item.notes}</p>
                  <Link className="text-link" to="/contact">
                    Ask about {item.name}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">Seasons</p>
              <h2 className="mb-3">When Tarangire is at its best</h2>
              <p className="page-lead mb-0">
                Dry months bring the densest gatherings. Green months trade some
                concentration for birds, soft light, and quieter tracks.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {seasons.map((item) => (
              <div className="col-md-6 col-lg-3" key={item.title}>
                <article className="approach-item h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">Before you go</p>
              <h2 className="mb-0">Practical notes from the desk</h2>
            </div>
          </div>

          <div className="row g-4">
            {practical.map((item) => (
              <div className="col-md-4" key={item.title}>
                <article className="approach-item h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-close">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <p className="eyebrow eyebrow--light">Plan your safari</p>
              <h2 className="mb-3">Tell us if Tarangire opens or closes your northern loop.</h2>
              <p className="mb-0 home-close__copy">
                We reply with night count, camp style, and how it connects to
                Manyara, Ngorongoro, Serengeti, or Zanzibar.
              </p>
            </div>
            <div className="col-lg-4 text-lg-end">
              <Link className="btn btn-mk-gold" to="/contact">
                Contact the desk
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Tarangire;
